/**
 * Gesture Detection Utilities
 * Reusable swipe and double-tap detection for touch and mouse
 */

export interface SwipeOptions {
  /** Minimum distance in pixels to trigger swipe (default: 50) */
  threshold?: number;
  /** Maximum time in ms to complete swipe (default: 300) */
  maxTime?: number;
  /** Callback for swipe left */
  onSwipeLeft?: () => void;
  /** Callback for swipe right */
  onSwipeRight?: () => void;
}

export interface DoubleTapOptions {
  /** Maximum time between taps in ms (default: 300) */
  maxInterval?: number;
  /** Callback for double-tap/double-click */
  onDoubleTap?: (e: Event) => void;
  /** Elements to ignore (e.g., buttons) - selector string */
  ignoreSelector?: string;
}

interface SwipeState {
  startX: number;
  startY: number;
  startTime: number;
  tracking: boolean;
}

/**
 * Setup swipe detection on an element
 * Detects horizontal swipes via touch and mouse drag
 * @returns Cleanup function to remove listeners
 */
export function setupSwipeDetection(
  element: HTMLElement,
  options: SwipeOptions
): () => void {
  const threshold = options.threshold ?? 50;
  const maxTime = options.maxTime ?? 300;

  const state: SwipeState = {
    startX: 0,
    startY: 0,
    startTime: 0,
    tracking: false,
  };

  function handleStart(x: number, y: number) {
    state.startX = x;
    state.startY = y;
    state.startTime = Date.now();
    state.tracking = true;
  }

  function handleEnd(x: number, y: number) {
    if (!state.tracking) return;
    state.tracking = false;

    const deltaX = x - state.startX;
    const deltaY = y - state.startY;
    const elapsed = Date.now() - state.startTime;

    // Check if swipe is fast enough and horizontal enough
    if (elapsed > maxTime) return;
    if (Math.abs(deltaX) < threshold) return;
    if (Math.abs(deltaY) > Math.abs(deltaX)) return; // More vertical than horizontal

    if (deltaX > 0) {
      options.onSwipeRight?.();
    } else {
      options.onSwipeLeft?.();
    }
  }

  function handleCancel() {
    state.tracking = false;
  }

  // Touch handlers
  function onTouchStart(e: TouchEvent) {
    if (e.touches.length === 1) {
      handleStart(e.touches[0].clientX, e.touches[0].clientY);
    }
  }

  function onTouchEnd(e: TouchEvent) {
    if (e.changedTouches.length === 1) {
      handleEnd(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
    }
  }

  function onTouchCancel() {
    handleCancel();
  }

  // Add listeners (touch only - swipe feels wrong on desktop)
  element.addEventListener('touchstart', onTouchStart, { passive: true });
  element.addEventListener('touchend', onTouchEnd, { passive: true });
  element.addEventListener('touchcancel', onTouchCancel, { passive: true });

  // Return cleanup function
  return () => {
    element.removeEventListener('touchstart', onTouchStart);
    element.removeEventListener('touchend', onTouchEnd);
    element.removeEventListener('touchcancel', onTouchCancel);
  };
}

/**
 * Setup double-tap/double-click detection on an element
 * @returns Cleanup function to remove listeners
 */
export function setupDoubleTapDetection(
  element: HTMLElement,
  options: DoubleTapOptions
): () => void {
  const maxInterval = options.maxInterval ?? 300;
  const ignoreSelector = options.ignoreSelector ?? 'button, a, .btn';

  let lastTapTime = 0;
  let lastTapTarget: EventTarget | null = null;

  function shouldIgnore(e: Event): boolean {
    const target = e.target as HTMLElement;
    if (!target) return false;
    // Check if target or any ancestor matches ignore selector
    return !!target.closest(ignoreSelector);
  }

  function handleTap(e: Event) {
    if (shouldIgnore(e)) return;

    const now = Date.now();
    const timeSinceLastTap = now - lastTapTime;

    if (timeSinceLastTap < maxInterval && lastTapTarget === e.target) {
      // Double tap detected
      options.onDoubleTap?.(e);
      lastTapTime = 0;
      lastTapTarget = null;
    } else {
      lastTapTime = now;
      lastTapTarget = e.target;
    }
  }

  // Touch handler
  function onTouchEnd(e: TouchEvent) {
    // Only single finger tap
    if (e.changedTouches.length !== 1) return;
    handleTap(e);
  }

  // Click handler for mouse
  function onClick(e: MouseEvent) {
    handleTap(e);
  }

  // Add listeners
  element.addEventListener('touchend', onTouchEnd, { passive: true });
  element.addEventListener('click', onClick);

  // Return cleanup function
  return () => {
    element.removeEventListener('touchend', onTouchEnd);
    element.removeEventListener('click', onClick);
  };
}
